import { useEffect, useRef, useState, type FC } from 'react';
import { Flag } from 'lucide-react';
import { logger } from '../utils/logger';
import type { DelegationActivationPayload, RecommendationReportReason } from '../types/index';
import { AdMeshViewabilityTracker } from './AdMeshViewabilityTracker';
import type { AdMeshTailAdProps } from './AdMeshTailAd';

const isValidUrl = (url: string): boolean => {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
};

const getDisplayText = (value: unknown): string => {
  if (typeof value === 'string') {
    return value.trim();
  }
  if (typeof value === 'number') {
    return String(value);
  }
  return '';
};

const asOptionalString = (value: unknown): string | undefined => {
  const text = getDisplayText(value);
  return text || undefined;
};

const getCTALabel = (ctaLabel?: string): string => {
  if (ctaLabel && ctaLabel.trim()) {
    return ctaLabel.trim();
  }
  return '';
};

const formatProductPrice = (price: unknown, currency?: string): string => {
  if (price === null || price === undefined || price === '') {
    return '';
  }

  if (typeof price === 'number') {
    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency || 'USD',
        maximumFractionDigits: 2,
      }).format(price);
    } catch {
      return String(price);
    }
  }

  return getDisplayText(price);
};

export const AdMeshCompactCard: FC<AdMeshTailAdProps> = ({
  recommendations,
  theme,
  className = '',
  style = {},
  sessionId,
  triggerExposureUrl = true,
  onDelegationConsent,
  onDelegationActivated,
  onStartDelegation,
  onRecommendationReport,
}) => {
  if (!recommendations || recommendations.length === 0) {
    logger.log('[AdMesh Compact Tail Ad] No recommendations provided - not rendering');
    return null;
  }

  const firstRecommendation = recommendations[0];
  const recommendation = firstRecommendation?.recommendation;
  const creative = recommendation?.creative;
  const tracking = firstRecommendation?.tracking;
  const delegation = firstRecommendation?.delegation;

  const recommendationId = firstRecommendation?.recommendation_id || '';
  const productId = recommendation?.offerId || '';
  const brandName = creative?.brand_name || '';
  const headline = creative?.headline || '';
  const description = creative?.description || '';
  const logoUrl = creative?.logo_url || '';
  const clickUrl = tracking?.click_url || creative?.landing_page_url || '';
  const exposureUrl = tracking?.exposure_url || '';
  const disclosure = recommendation?.disclosure || 'Sponsored';
  const relatedProducts = creative?.products || [];
  const visibleProducts = relatedProducts.slice(0, 4);
  const hasProductRail = relatedProducts.length > 1;
  const recommendationFormat = recommendation?.format || 'tail';
  const isBridgeRecommendation = recommendationFormat === 'bridge';
  const hasDelegation = delegation?.available === true;
  const shouldShowDelegationButton = isBridgeRecommendation && hasDelegation;
  const delegationBrandName = delegation?.brand_name || brandName;
  const delegationCtaText = 'Talk to Agent';
  const ctaLabel = getCTALabel(creative?.cta_text);
  const shouldShowPrimaryCta = !hasProductRail && !!ctaLabel && !!clickUrl;

  if (!brandName && !headline && !description) {
    logger.log('[AdMesh Compact Tail Ad] No valid recommendation data provided - not rendering', {
      recommendationId,
    });
    return null;
  }

  const compactTitle = headline && headline !== brandName ? headline : brandName;
  const compactSummary = description;
  const normalizedBrandName = brandName.trim().toLowerCase();
  const normalizedCompactTitle = compactTitle.trim().toLowerCase();
  const shouldShowBrandEyebrow = Boolean(brandName) && normalizedBrandName !== normalizedCompactTitle;

  const requestDefaultConsent = (): boolean => {
    return true;
  };

  const [showConsentPrompt, setShowConsentPrompt] = useState(false);
  const [isConsentConnecting, setIsConsentConnecting] = useState(false);
  const [reportMenuOpen, setReportMenuOpen] = useState(false);
  const [submittedReportReason, setSubmittedReportReason] = useState<RecommendationReportReason | null>(null);
  const consentResolverRef = useRef<((value: boolean) => void) | null>(null);

  useEffect(() => {
    return () => {
      if (consentResolverRef.current) {
        consentResolverRef.current(false);
        consentResolverRef.current = null;
      }
    };
  }, []);

  const resolveConsentPrompt = (value: boolean) => {
    if (value) {
      setIsConsentConnecting(true);
    }
    consentResolverRef.current?.(value);
    consentResolverRef.current = null;
    if (!value) {
      setIsConsentConnecting(false);
      setShowConsentPrompt(false);
    }
  };

  const requestConsent = async (): Promise<boolean> => {
    if (onDelegationConsent) {
      return await onDelegationConsent(firstRecommendation);
    }
    if (!requestDefaultConsent()) {
      return false;
    }
    return await new Promise<boolean>((resolve) => {
      consentResolverRef.current = resolve;
      setShowConsentPrompt(true);
    });
  };

  const handleContainerClick = (source: string, e?: React.MouseEvent) => {
    if (e) {
      e.stopPropagation();
    }

    if (typeof window !== 'undefined' && (window as any).admeshTracker) {
      (window as any).admeshTracker.trackClick({
        recommendationId,
        productId,
        clickUrl,
        source,
      }).catch(() => {
        logger.error(`[AdMesh] Failed to track ${source} click`);
      });
    }
  };

  const reportReasons: Array<{ value: RecommendationReportReason; label: string }> = [
    { value: 'irrelevant', label: 'Not relevant' },
    { value: 'misleading', label: 'Misleading' },
    { value: 'inappropriate', label: 'Inappropriate' },
    { value: 'broken_link', label: 'Broken link' },
    { value: 'other', label: 'Other' },
  ];

  const handleReportButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    if (submittedReportReason) {
      return;
    }
    setReportMenuOpen((current) => !current);
  };

  const handleReportReasonClick = async (
    event: React.MouseEvent<HTMLButtonElement>,
    reason: RecommendationReportReason,
  ) => {
    event.preventDefault();
    event.stopPropagation();
    setReportMenuOpen(false);
    setSubmittedReportReason(reason);

    if (!onRecommendationReport) {
      return;
    }

    try {
      await onRecommendationReport({
        recommendation: firstRecommendation,
        recommendation_id: recommendationId,
        reason,
        session_id: sessionId,
      });
    } catch (error) {
      logger.warn('[AdMesh Compact Tail Ad] Failed to submit recommendation report:', error);
    }
  };

  const handleCardClick = () => {
    if (hasProductRail || !clickUrl) {
      return;
    }
    handleContainerClick('compact_tail_ad_card');
    if (typeof window !== 'undefined') {
      window.open(clickUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const handleCardKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (hasProductRail || !clickUrl) {
      return;
    }
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleCardClick();
    }
  };

  const trackDelegationEngagement = async () => {
    if (!(recommendationId && sessionId)) {
      return;
    }

    try {
      const apiBaseUrl =
        (typeof window !== 'undefined' && (window as any).__ADMESH_API_BASE_URL__) ||
        'https://api.useadmesh.com';

      const response = await fetch(`${apiBaseUrl}/click/bridge-engagement`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          recommendation_id: recommendationId,
          session_id: sessionId,
          is_test: false,
        }),
      });

      if (!response.ok) {
        logger.warn('[AdMesh Compact Tail Ad] Delegation engagement tracking failed:', response.statusText);
      }
    } catch (error) {
      logger.error('[AdMesh Compact Tail Ad] Error tracking delegation engagement:', error);
    }
  };

  const handleDelegationClick = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (!firstRecommendation || !shouldShowDelegationButton) {
      return;
    }

    handleContainerClick('compact_tail_ad_delegation_cta');

    const consentGranted = await requestConsent();
    if (!consentGranted) {
      return;
    }

    void trackDelegationEngagement();

    const delegationPayload: DelegationActivationPayload | null =
      delegation?.available
        ? {
            recommendation_id: firstRecommendation.recommendation_id,
            brand_id: asOptionalString(delegation.brand_id),
            brand_name: asOptionalString(delegation.brand_name || creative?.brand_name),
            mcp_endpoint: asOptionalString(delegation.mcp_endpoint),
            eligibility_reason: asOptionalString(delegation.eligibility_reason),
            delegate_reason: asOptionalString(delegation.delegate_reason),
            session_goal: asOptionalString(delegation.session_goal),
            desired_outcome_type: delegation.desired_outcome_type,
            required_user_inputs: Array.isArray(delegation.required_user_inputs)
              ? delegation.required_user_inputs.filter((value): value is string => typeof value === 'string')
              : [],
            required_consent_types: Array.isArray(delegation.required_consent_types)
              ? delegation.required_consent_types.filter((value): value is string => typeof value === 'string')
              : [],
            success_outcome: asOptionalString(delegation.success_outcome),
            fallback_message: asOptionalString(delegation.fallback_message),
            session_mode: asOptionalString(delegation.session_mode),
            status: asOptionalString(delegation.status),
            capabilities: delegation.capabilities || [],
            allowed_data_types: delegation.allowed_data_types || [],
            consent_requirements: delegation.consent_requirements || [],
            auth_model: asOptionalString(delegation.auth_model),
            handoff_protocol:
              delegation.handoff_protocol && typeof delegation.handoff_protocol === 'object'
                ? delegation.handoff_protocol
                : undefined,
            supported_actions: delegation.supported_actions || [],
            metadata:
              delegation.metadata && typeof delegation.metadata === 'object'
                ? delegation.metadata
                : undefined,
            platform_response: firstRecommendation,
          }
        : null;

    if (onDelegationActivated && delegationPayload) {
      try {
        await onDelegationActivated(delegationPayload);
      } finally {
        setIsConsentConnecting(false);
        setShowConsentPrompt(false);
      }
      return;
    }

    if (onStartDelegation) {
      try {
        await onStartDelegation(firstRecommendation);
      } finally {
        setIsConsentConnecting(false);
        setShowConsentPrompt(false);
      }
      return;
    }

    setIsConsentConnecting(false);
    setShowConsentPrompt(false);

  };

  const [logoError, setLogoError] = useState(false);

  const isDarkMode =
    theme?.mode === 'dark' ||
    (typeof window !== 'undefined' &&
      window.matchMedia &&
      window.matchMedia('(prefers-color-scheme: dark)').matches);

  const cardBackground =
    theme?.surfaceColor ||
    theme?.components?.card?.backgroundColor ||
    (isDarkMode ? '#111827' : '#ffffff');
  const cardBorder =
    theme?.borderColor ||
    theme?.components?.card?.borderColor ||
    (isDarkMode ? 'rgba(255, 255, 255, 0.10)' : 'rgba(15, 23, 42, 0.08)');
  const cardShadow =
    theme?.shadows?.medium ||
    theme?.components?.card?.boxShadow ||
    (isDarkMode
      ? '0 8px 18px rgba(0, 0, 0, 0.28)'
      : '0 8px 18px rgba(15, 23, 42, 0.08)');
  const accentColor = '#2563eb';
  const mutedText = isDarkMode ? '#9ca3af' : '#6b7280';
  const bodyText = isDarkMode ? '#e5e7eb' : '#374151';
  const titleText = isDarkMode ? '#f9fafb' : '#0f172a';
  const railCardBackground = isDarkMode ? 'rgba(255, 255, 255, 0.04)' : '#f8fafc';
  const textColumnOffset = '50px';
  const footerAction =
    shouldShowDelegationButton || shouldShowPrimaryCta ? (
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
        {shouldShowDelegationButton ? (
          <button
            type="button"
            onClick={handleDelegationClick}
            style={{
              background: 'none',
              border: 'none',
              padding: 0,
              color: accentColor,
              fontSize: '12px',
              fontWeight: 500,
              cursor: 'pointer',
              textDecoration: 'none',
            }}
          >
            {delegationCtaText}
          </button>
        ) : null}
        {shouldShowPrimaryCta ? (
          <a
            href={clickUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => handleContainerClick('compact_tail_ad_cta', e)}
            style={{
              color: accentColor,
              fontSize: '12px',
              fontWeight: 600,
              textDecoration: 'none',
            }}
          >
            {ctaLabel}
          </a>
        ) : null}
      </div>
    ) : null;

  return (
    <AdMeshViewabilityTracker
      productId={productId}
      recommendationId={recommendationId}
      exposureUrl={exposureUrl}
      sessionId={sessionId}
      triggerExposureUrl={triggerExposureUrl}
      className={`admesh-compact-tail-ad ${className}`}
      style={{
        all: 'initial',
        boxSizing: 'border-box',
        display: 'block',
        fontFamily: theme?.fontFamily || '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        fontSize: '14px',
        lineHeight: 1.4,
        ...style,
      }}
    >
      <style>{`
        .admesh-compact-tail-ad,
        .admesh-compact-tail-ad * {
          box-sizing: border-box;
        }

        .admesh-compact-line-clamp-1 {
          overflow: hidden;
          display: -webkit-box;
          -webkit-box-orient: vertical;
          -webkit-line-clamp: 1;
        }

        .admesh-compact-line-clamp-2 {
          overflow: hidden;
          display: -webkit-box;
          -webkit-box-orient: vertical;
          -webkit-line-clamp: 2;
        }

        .admesh-compact-tail-scroll {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }

        .admesh-compact-tail-scroll::-webkit-scrollbar {
          display: none;
        }

        @media (max-width: 420px) {
          .admesh-compact-meta-row {
            align-items: flex-start !important;
          }

          .admesh-compact-disclosure-actions {
            gap: 4px !important;
          }

          .admesh-compact-footer-row,
          .admesh-compact-report-panel,
          .admesh-compact-report-status {
            margin-left: 0 !important;
          }

          .admesh-compact-report-panel {
            width: 100%;
          }

          .admesh-compact-report-option {
            flex: 1 1 calc(50% - 6px);
            min-height: 34px;
          }
        }
      `}</style>
      <div
        role={!hasProductRail && clickUrl ? 'link' : undefined}
        tabIndex={!hasProductRail && clickUrl ? 0 : undefined}
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '10px',
          padding: '12px',
          borderRadius: theme?.borderRadius || theme?.components?.card?.borderRadius || '14px',
          border: `1px solid ${cardBorder}`,
          background: cardBackground,
          boxShadow: cardShadow,
          minWidth: 0,
          cursor: !hasProductRail && clickUrl ? 'pointer' : undefined,
        }}
        onClick={handleCardClick}
        onKeyDown={handleCardKeyDown}
      >
        <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start', minWidth: 0 }}>
          <div
            style={{
              width: '40px',
              height: '40px',
              borderRadius: '10px',
              flexShrink: 0,
              overflow: 'hidden',
              background: isDarkMode ? '#1f2937' : '#eff6ff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {!logoError && logoUrl && isValidUrl(logoUrl) ? (
              <img
                src={logoUrl}
                alt={`${brandName} logo`}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                  padding: '6px',
                  background: '#ffffff',
                }}
                onError={() => setLogoError(true)}
              />
            ) : (
              <span style={{ fontSize: '14px', fontWeight: 700, color: mutedText }}>
                {(brandName || 'A').charAt(0).toUpperCase()}
              </span>
            )}
          </div>

          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              className="admesh-compact-meta-row"
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '8px',
                marginBottom: '4px',
              }}
            >
              <div className="admesh-compact-line-clamp-1" style={{ fontSize: '11px', color: mutedText }}>
                {shouldShowBrandEyebrow ? brandName : ''}
              </div>
              <span
                style={{
                  flexShrink: 0,
                  fontSize: '10px',
                  color: mutedText,
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                }}
              >
                {disclosure}
              </span>
            </div>

            {!hasProductRail && (
              <div
                className="admesh-compact-line-clamp-2"
                style={{
                  fontSize: '14px',
                  lineHeight: 1.3,
                  fontWeight: 700,
                  color: titleText,
                  marginBottom: '4px',
                }}
                title={compactTitle}
              >
                {compactTitle}
              </div>
            )}

            {compactSummary && (
              <p
                className="admesh-compact-line-clamp-2"
                style={{
                  margin: 0,
                  fontSize: '12px',
                  lineHeight: 1.35,
                  color: bodyText,
                }}
                title={compactSummary}
              >
                {compactSummary}
              </p>
            )}
          </div>
        </div>

        {hasProductRail && (
          <div
            className="admesh-compact-tail-scroll"
            style={{
              display: 'flex',
              gap: '8px',
              overflowX: 'auto',
              paddingBottom: '2px',
            }}
          >
            {visibleProducts.map((product) => {
              const productName = getDisplayText(product.product_name) || 'Product';
              const productLink =
                getDisplayText(product.product_link) ||
                getDisplayText(product.product_url) ||
                clickUrl ||
                '#';
              const productPrice = formatProductPrice(product.product_price, product.product_currency);
              const productImageUrl = getDisplayText(product.product_image_url);
              const displayImage = productImageUrl && isValidUrl(productImageUrl) ? productImageUrl : '';
              const hasProductLink = productLink !== '#';

              return (
                <a
                  key={product.product_id || `${recommendationId}-${productName}`}
                  href={productLink}
                  target={hasProductLink ? '_blank' : undefined}
                  rel={hasProductLink ? 'noopener noreferrer' : undefined}
                  onClick={(e) => handleContainerClick(`compact_tail_ad_product_${product.product_id || 'item'}`, e)}
                  style={{
                    minWidth: '220px',
                    maxWidth: '220px',
                    display: 'block',
                    textDecoration: 'none',
                    color: 'inherit',
                    border: `1px solid ${cardBorder}`,
                    borderRadius: '12px',
                    background: railCardBackground,
                    padding: '8px',
                    flexShrink: 0,
                  }}
                >
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                    <div
                      style={{
                        width: '34px',
                        height: '34px',
                        borderRadius: '8px',
                        overflow: 'hidden',
                        flexShrink: 0,
                        background: isDarkMode ? '#374151' : '#e5e7eb',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: mutedText,
                        fontSize: '12px',
                        fontWeight: 700,
                      }}
                    >
                      {displayImage ? (
                        <img src={displayImage} alt={productName} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      ) : (
                        productName.charAt(0).toUpperCase()
                      )}
                    </div>

                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div
                          style={{
                            fontSize: '12px',
                            lineHeight: 1.25,
                            fontWeight: 600,
                            color: titleText,
                            marginBottom: '4px',
                            whiteSpace: 'normal',
                            wordBreak: 'break-word',
                          }}
                          title={productName}
                        >
                          {productName}
                        </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                        {productPrice && (
                          <span style={{ fontSize: '12px', fontWeight: 700, color: titleText }}>
                            {productPrice}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </a>
              );
            })}
          </div>
        )}

        <div
          className="admesh-compact-footer-row"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '8px',
            flexWrap: 'wrap',
            marginLeft: hasProductRail ? 0 : textColumnOffset,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0, flexWrap: 'wrap' }}>
            {footerAction}
          </div>

          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              gap: '8px',
              marginLeft: 'auto',
              flexShrink: 0,
            }}
          >
            {hasProductRail && relatedProducts.length > visibleProducts.length ? (
              <span style={{ fontSize: '10px', color: mutedText }}>
                +{relatedProducts.length - visibleProducts.length} more
              </span>
            ) : null}
            <button
              type="button"
              aria-label={submittedReportReason ? 'Recommendation reported' : 'Report recommendation'}
              title={submittedReportReason ? 'Reported' : 'Report recommendation'}
              disabled={!!submittedReportReason}
              onClick={handleReportButtonClick}
              style={{
                width: '28px',
                height: '28px',
                borderRadius: '999px',
                border: `1px solid ${cardBorder}`,
                background: submittedReportReason ? railCardBackground : cardBackground,
                color: submittedReportReason ? accentColor : mutedText,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 0,
                cursor: submittedReportReason ? 'default' : 'pointer',
                flexShrink: 0,
              }}
            >
              <Flag size={13} />
            </button>
          </div>
        </div>

        {reportMenuOpen ? (
          <div
            className="admesh-compact-report-panel"
            onClick={(event) => event.stopPropagation()}
            style={{
              marginLeft: hasProductRail ? 0 : textColumnOffset,
              border: `1px solid ${cardBorder}`,
              borderRadius: '12px',
              background: railCardBackground,
              padding: '8px',
              display: 'flex',
              flexWrap: 'wrap',
              gap: '6px',
            }}
          >
            {reportReasons.map((reason) => (
              <button
                key={reason.value}
                className="admesh-compact-report-option"
                type="button"
                onClick={(event) => handleReportReasonClick(event, reason.value)}
                style={{
                  borderRadius: '999px',
                  border: `1px solid ${cardBorder}`,
                  background: cardBackground,
                  color: titleText,
                  fontSize: '11px',
                  fontWeight: 600,
                  padding: '6px 9px',
                  cursor: 'pointer',
                }}
              >
                {reason.label}
              </button>
            ))}
          </div>
        ) : null}

        {submittedReportReason ? (
          <div
            className="admesh-compact-report-status"
            style={{
              marginLeft: hasProductRail ? 0 : textColumnOffset,
              fontSize: '11px',
              color: mutedText,
            }}
          >
            Thanks for reporting this recommendation.
          </div>
        ) : null}

        {showConsentPrompt ? (
          <div
            style={{
              marginTop: '2px',
              border: `1px solid ${cardBorder}`,
              borderRadius: '12px',
              background: railCardBackground,
              padding: '12px',
              display: 'flex',
              flexDirection: 'column',
              gap: '10px',
            }}
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <div
                style={{
                  fontSize: '11px',
                  fontWeight: 700,
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                  color: accentColor,
                }}
              >
                Confirm Agent Access
              </div>
              <div style={{ fontSize: '13px', lineHeight: 1.4, color: titleText, fontWeight: 600 }}>
                Allow {delegationBrandName || 'this brand'} to start a delegated session?
              </div>
              <div style={{ fontSize: '12px', lineHeight: 1.45, color: bodyText }}>
                The agent will only use the details you choose to share for this request.
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
              <button
                type="button"
                disabled={isConsentConnecting}
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  if (isConsentConnecting) {
                    return;
                  }
                  resolveConsentPrompt(false);
                }}
                style={{
                  borderRadius: '10px',
                  border: `1px solid ${cardBorder}`,
                  background: cardBackground,
                  color: titleText,
                  fontSize: '12px',
                  fontWeight: 600,
                  padding: '8px 12px',
                  cursor: isConsentConnecting ? 'not-allowed' : 'pointer',
                  opacity: isConsentConnecting ? 0.65 : 1,
                }}
              >
                Not now
              </button>
              <button
                type="button"
                disabled={isConsentConnecting}
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  if (isConsentConnecting) {
                    return;
                  }
                  resolveConsentPrompt(true);
                }}
                style={{
                  borderRadius: '10px',
                  border: 'none',
                  background: accentColor,
                  color: '#ffffff',
                  fontSize: '12px',
                  fontWeight: 700,
                  padding: '8px 12px',
                  cursor: isConsentConnecting ? 'wait' : 'pointer',
                  opacity: isConsentConnecting ? 0.86 : 1,
                }}
              >
                {isConsentConnecting ? 'Connecting...' : 'Allow agent'}
              </button>
            </div>
          </div>
        ) : null}
      </div>
    </AdMeshViewabilityTracker>
  );
};

export default AdMeshCompactCard;
